import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ReviewsOutlinedIcon from '@mui/icons-material/ReviewsOutlined';
import { toast } from 'react-toastify';
import api from '../../../services/api';
import {
  answerFeedbackIfoodReview,
  getFeedbackIfoodSyncStatus,
  getGoogleBusinessStatus,
  getIfoodReviewsAvailability,
  listGoogleBusinessReviews,
  replyGoogleBusinessReview,
  syncFeedbackIfoodReviews,
} from '../../../services/api';
import { getGoogleBusinessErrorInfo } from '../../../utils/googleBusinessError';
import { getIfoodErrorMessage } from '../../../utils/ifoodError';
import FeedbackResumoCard from './FeedbackResumoCard';
import FeedbackSentimentDistribution from './FeedbackSentimentDistribution';
import FeedbackOrigemCard from './FeedbackOrigemCard';
import FeedbackList from './FeedbackList';
import FeedbackFilters, { DEFAULT_FEEDBACK_PERIOD } from './FeedbackFilters';
import Loader from '../ClubeDeMesa/Loader';
import FeedbackPointsChart from './FeedbackPointsChart';
import { feedbackHeroTokens } from './feedbackHeroTokens';
import {
  buildTransientSummary,
  filterFeedbacks,
  mergeGoogleFeedbackPages,
  sortFeedbacks,
} from './googleLiveFeedback';
import './FeedbackHero.css';

const DEFAULT_PAGE_SIZE = 10;
const defaultFilters = {
  periodo: DEFAULT_FEEDBACK_PERIOD,
  sentimento: 'all',
  nota: 'all',
  origem: 'all',
};

const formatSyncDateTime = (value) => {
  if (!value) return 'Não informado';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Não informado';
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
};

const FeedbackHero = () => {
  const [resumo, setResumo] = useState(null);
  const [feedbacks, setFeedbacks] = useState([]);
  const [allFeedbacks, setAllFeedbacks] = useState([]);
  const [pontos, setPontos] = useState(null);
  const [filters, setFilters] = useState(defaultFilters);
  const [ordem, setOrdem] = useState('desc');
  const [page, setPage] = useState(1);
  const [attentionOnly, setAttentionOnly] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [resolvingFeedbackId, setResolvingFeedbackId] = useState(null);
  const [loading, setLoading] = useState(true);
  const firstLoadRef = useRef(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [highlightId, setHighlightId] = useState(null);
  const deepLinkPendingRef = useRef(false);
  const deepLinkWantsAttentionRef = useRef(false);
  const deepLinkAttemptsRef = useRef(0);
  const silentIfoodSyncStartedRef = useRef(false);
  const ifoodSyncInFlightRef = useRef(false);
  const navigate = useNavigate();
  const [ifoodAvailable, setIfoodAvailable] = useState(null);
  const [ifoodSyncState, setIfoodSyncState] = useState(null);
  const [syncingIfood, setSyncingIfood] = useState(false);
  const [ifoodSyncResult, setIfoodSyncResult] = useState(null);
  const [googleLive, setGoogleLive] = useState({
    checked: false,
    connected: false,
    ready: false,
    loading: false,
    reviews: [],
    nextPageToken: null,
    totalReviewCount: 0,
    error: '',
  });

  useEffect(() => {
    let active = true;

    const loadGoogleReviews = async () => {
      setGoogleLive((current) => ({ ...current, loading: true, error: '' }));
      try {
        const googleStatus = await getGoogleBusinessStatus();
        const ready = Boolean(googleStatus?.connected && googleStatus?.location_selected);
        if (!active) return;
        if (!ready) {
          setGoogleLive((current) => ({
            ...current,
            checked: true,
            connected: Boolean(googleStatus?.connected),
            ready: false,
            loading: false,
            reviews: [],
            nextPageToken: null,
            totalReviewCount: 0,
          }));
          return;
        }

        const response = await listGoogleBusinessReviews({ pageSize: 50, orderBy: 'updateTime desc' });
        if (!active) return;
        setGoogleLive({
          checked: true,
          connected: true,
          ready: true,
          loading: false,
          reviews: mergeGoogleFeedbackPages([], response?.reviews || []),
          nextPageToken: response?.next_page_token || null,
          totalReviewCount: Number(response?.total_review_count || 0),
          error: '',
        });
      } catch (error) {
        if (!active) return;
        const info = getGoogleBusinessErrorInfo(error, 'Não foi possível consultar as avaliações do Google.');
        setGoogleLive((current) => ({
          ...current,
          checked: true,
          loading: false,
          error: info.message,
        }));
      }
    };

    loadGoogleReviews();
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (silentIfoodSyncStartedRef.current) return;
    silentIfoodSyncStartedRef.current = true;
    let active = true;

    const prepareIfoodSync = async () => {
      let connected = false;
      let syncState = null;
      try {
        const [availability, currentSyncState] = await Promise.all([
          getIfoodReviewsAvailability(),
          getFeedbackIfoodSyncStatus(),
        ]);
        if (!active) return;
        connected = Boolean(availability?.connected && availability?.merchant?.id);
        syncState = currentSyncState;
        setIfoodAvailable(connected);
        setIfoodSyncState(syncState);
      } catch (error) {
        console.warn('Não foi possível verificar a integração com o iFood:', error);
        setIfoodAvailable(false);
        return;
      }

      if (!connected || !syncState?.enabled) return;
      if (ifoodSyncInFlightRef.current) return;
      ifoodSyncInFlightRef.current = true;
      setSyncingIfood(true);
      try {
        const result = await syncFeedbackIfoodReviews({ mode: 'silent' });
        if (!active) return;
        if (!result?.skipped && (
          Number(result?.imported || 0) > 0
          || Number(result?.classified_feedbacks || 0) > 0
        )) {
          setRefreshKey((value) => value + 1);
        }
        setIfoodSyncState(result?.state || syncState);
      } catch (error) {
        console.warn('Não foi possível atualizar avaliações do iFood em segundo plano:', error);
      } finally {
        ifoodSyncInFlightRef.current = false;
        if (active) setSyncingIfood(false);
      }
    };

    prepareIfoodSync();
    return () => { active = false; };
  }, []);

  // Deep-link vindo da notificação de feedback ruim no WhatsApp:
  // /feedback-hero?atencao=1&feedback=<id> aplica o filtro "precisam de atenção"
  // e destaca/rola até o feedback. Roda uma única vez ao montar.
  useEffect(() => {
    if (deepLinkPendingRef.current) return;
    const atencao = searchParams.get('atencao');
    const feedbackParam = searchParams.get('feedback');
    const wantsAttention = atencao === '1' || atencao === 'true';
    if (!wantsAttention && !feedbackParam) return;

    deepLinkPendingRef.current = true;
    deepLinkWantsAttentionRef.current = wantsAttention;
    if (feedbackParam) setHighlightId(String(feedbackParam));
    if (wantsAttention) {
      setFilters((prev) => ({ ...prev, sentimento: 'neg', nota: 'all' }));
      setAttentionOnly(true);
      setPage(1);
    }

    // Limpa os parâmetros da URL para não reaplicar em refresh/re-render.
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('atencao');
    nextParams.delete('feedback');
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // Após carregar a lista (já com o filtro aplicado), rola até a seção de
  // feedbacks e destaca o feedback alvo. Reexecuta quando a lista muda, então
  // o destaque acontece assim que o card do feedback aparece (até ~6 tentativas).
  useEffect(() => {
    if (!deepLinkPendingRef.current || loading) return;
    if (deepLinkWantsAttentionRef.current && !attentionOnly) return;

    const timer = setTimeout(() => {
      const targetCard = highlightId
        ? document.getElementById(`feedback-${highlightId}`)
        : null;
      const section = document.getElementById('feedbacks-list-section');

      if (targetCard) {
        targetCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
        targetCard.classList.add('feedback-card-deeplink-highlight');
        setTimeout(() => {
          targetCard.classList.remove('feedback-card-deeplink-highlight');
        }, 3200);
        deepLinkPendingRef.current = false;
        return;
      }

      // Card ainda não está na lista renderizada: rola até a seção e tenta de
      // novo nas próximas renderizações, desistindo após algumas tentativas.
      section?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      deepLinkAttemptsRef.current += 1;
      if (!highlightId || deepLinkAttemptsRef.current >= 6) {
        deepLinkPendingRef.current = false;
      }
    }, 220);

    return () => clearTimeout(timer);
  }, [loading, attentionOnly, feedbacks, highlightId]);

  useEffect(() => {
    let active = true;

    const buildSummaryParams = () => {
      const params = {};
      if (filters.periodo !== 'all') params.periodo = filters.periodo;
      if (filters.sentimento !== 'all') params.sentimento = filters.sentimento;
      if (filters.nota !== 'all') params.nota = filters.nota;
      if (filters.origem !== 'all') params.origem = filters.origem;
      return params;
    };

    const buildListParams = () => {
      const params = {
        page,
        pageSize: DEFAULT_PAGE_SIZE,
        ordem,
      };
      if (filters.periodo !== 'all') params.periodo = filters.periodo;
      if (filters.sentimento !== 'all') params.sentimento = filters.sentimento;
      if (filters.nota !== 'all') params.nota = filters.nota;
      if (filters.origem !== 'all') params.origem = filters.origem;
      if (attentionOnly) params.pendencia = 'aberta';
      return params;
    };

    const buildAllListParams = () => {
      const params = { ordem };
      if (filters.periodo !== 'all') params.periodo = filters.periodo;
      if (filters.sentimento !== 'all') params.sentimento = filters.sentimento;
      if (filters.nota !== 'all') params.nota = filters.nota;
      // Não aplicar filtro de origem aqui — queremos manter o breakdown completo no card "Origem dos feedbacks"
      return params;
    };

    const fetchData = async () => {
      try {
        const [resResumo, resFeedbacks, resPontos, resAll] = await Promise.all([
          api.get('/feedback/feedbacks/resumo', { params: buildSummaryParams() }),
          api.get('/feedback/feedbacks', { params: buildListParams() }),
          api.get('/feedback/feedbacks/pontos', { params: buildSummaryParams() }),
          api.get('/feedback/feedbacks', { params: buildAllListParams() }),
        ]);

        if (!active) return;
        setResumo(resResumo.data);
        setFeedbacks(resFeedbacks.data?.data || []);
        setPontos(resPontos.data?.data || []);
        setAllFeedbacks(Array.isArray(resAll.data) ? resAll.data : (resAll.data?.data || []));
      } catch (err) {
        console.error('Erro ao buscar feedbacks:', err);
      } finally {
        if (active && firstLoadRef.current) {
          setLoading(false);
          firstLoadRef.current = false;
        }
      }
    };

    fetchData();
    return () => {
      active = false;
    };
  }, [filters, ordem, page, attentionOnly, refreshKey]);

  const dashboardData = useMemo(() => {
    const localSummaryItems = filterFeedbacks(allFeedbacks, filters);
    const googleSummaryItems = filterFeedbacks(googleLive.reviews, filters);
    const summaryItems = [...localSummaryItems, ...googleSummaryItems];

    const localListItems = filterFeedbacks(allFeedbacks, filters, { attentionOnly });
    const googleListItems = filterFeedbacks(googleLive.reviews, filters, { attentionOnly });
    const sortedItems = sortFeedbacks([...localListItems, ...googleListItems], ordem);
    const offset = (page - 1) * DEFAULT_PAGE_SIZE;

    const originFilters = { ...filters, origem: 'all' };
    const originItems = [
      ...filterFeedbacks(allFeedbacks, originFilters),
      ...filterFeedbacks(googleLive.reviews, originFilters),
    ];

    return {
      feedbacks: sortedItems.slice(offset, offset + DEFAULT_PAGE_SIZE),
      total: sortedItems.length,
      summary: buildTransientSummary(summaryItems),
      origins: originItems,
    };
  }, [allFeedbacks, attentionOnly, filters, googleLive.reviews, ordem, page]);

  if (loading) return <Loader />;
  if (!resumo) return null;

  const { fonts, colors } = feedbackHeroTokens;
  const ifoodSyncReviewCount = Number(
    ifoodSyncResult?.received_reviews_count ?? ifoodSyncResult?.imported ?? 0
  );
  const ifoodSyncClassifiedCount = Number(ifoodSyncResult?.classified_feedbacks || 0);

  const handleFiltersChange = (patch) => {
    setFilters((prev) => ({ ...prev, ...patch }));
    if (Object.prototype.hasOwnProperty.call(patch, 'sentimento')
      || Object.prototype.hasOwnProperty.call(patch, 'nota')) {
      setAttentionOnly(false);
    }
    setPage(1);
  };

  const handleOrderChange = (value) => {
    setOrdem(value);
    setPage(1);
  };

  const handleClearFilters = () => {
    setFilters({ ...defaultFilters });
    setAttentionOnly(false);
    setPage(1);
  };

  const handleShowAttention = () => {
    setFilters((prev) => ({ ...prev, sentimento: 'neg', nota: 'all' }));
    setAttentionOnly(true);
    setPage(1);
    setTimeout(() => {
      document
        .getElementById('feedbacks-list-section')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 120);
  };

  const handleClearAttentionMode = () => {
    setFilters((prev) => ({ ...prev, sentimento: 'all' }));
    setAttentionOnly(false);
    setPage(1);
  };

  const handleMarkFeedbackAtendido = async (feedbackId) => {
    if (!feedbackId) return;

    setResolvingFeedbackId(feedbackId);
    try {
      const response = await api.patch(`/feedback/feedbacks/${feedbackId}/atendimento`, {
        atendido: true,
      });
      const updatedFeedback = response.data?.feedback || {
        id: feedbackId,
        pendencia_atendida: true,
      };
      const isSameFeedback = (fb) => String(fb.id) === String(feedbackId);
      const mergeFeedback = (fb) => (isSameFeedback(fb) ? { ...fb, ...updatedFeedback } : fb);

      setFeedbacks((prev) => {
        const updated = prev.map(mergeFeedback);
        return attentionOnly ? updated.filter((fb) => !isSameFeedback(fb)) : updated;
      });
      setAllFeedbacks((prev) => prev.map(mergeFeedback));
      setResumo((prev) => {
        if (!prev || Number(prev.pendencias_atencao || 0) <= 0) return prev;
        return {
          ...prev,
          pendencias_atencao: Math.max(0, Number(prev.pendencias_atencao || 0) - 1),
        };
      });

      toast.success('Feedback marcado como atendido.');
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      console.error('Erro ao marcar feedback como atendido:', error);
      toast.error('Não foi possível marcar o feedback como atendido.');
    } finally {
      setResolvingFeedbackId(null);
    }
  };

  const handleSyncIfood = async () => {
    if (ifoodAvailable === false) {
      navigate('/configuracoes/ifood');
      return;
    }
    if (ifoodAvailable === null) return;
    if (ifoodSyncInFlightRef.current) return;

    ifoodSyncInFlightRef.current = true;
    setSyncingIfood(true);
    try {
      const result = await syncFeedbackIfoodReviews({
        mode: ifoodSyncState?.enabled && ifoodSyncState?.last_review_created_at
          ? 'incremental'
          : 'full',
        force: true,
      });
      setIfoodSyncState(result?.state || await getFeedbackIfoodSyncStatus());
      setRefreshKey((value) => value + 1);
      setIfoodSyncResult(result);
      const count = Number(result?.received_reviews_count ?? result?.imported ?? 0);
      const classifiedCount = Number(result?.classified_feedbacks || 0);
      toast.success(count
        ? `${count} ${count === 1 ? 'avaliação recebida' : 'avaliações recebidas'} do iFood.`
        : classifiedCount
          ? `${classifiedCount} ${classifiedCount === 1 ? 'avaliação analisada' : 'avaliações analisadas'} para atualizar os temas.`
          : 'Consulta ao iFood concluída. Nenhuma avaliação foi retornada.');
    } catch (error) {
      toast.error(getIfoodErrorMessage(error, 'Não foi possível trazer as avaliações do iFood.'));
    } finally {
      ifoodSyncInFlightRef.current = false;
      setSyncingIfood(false);
    }
  };

  const handleRefreshGoogle = async () => {
    if (!googleLive.ready) {
      navigate('/feedback-hero/configuracoes?integracao=google');
      return;
    }
    setGoogleLive((current) => ({ ...current, loading: true, error: '' }));
    try {
      const response = await listGoogleBusinessReviews({ pageSize: 50, orderBy: 'updateTime desc' });
      setGoogleLive((current) => ({
        ...current,
        loading: false,
        reviews: mergeGoogleFeedbackPages([], response?.reviews || []),
        nextPageToken: response?.next_page_token || null,
        totalReviewCount: Number(response?.total_review_count || 0),
        error: '',
      }));
      setPage(1);
      toast.success('Avaliações do Google atualizadas ao vivo.');
    } catch (error) {
      const info = getGoogleBusinessErrorInfo(error, 'Não foi possível atualizar as avaliações do Google.');
      setGoogleLive((current) => ({ ...current, loading: false, error: info.message }));
      toast.error(info.message);
    }
  };

  const handleAnswerIfood = async (feedbackId, text) => {
    const result = await answerFeedbackIfoodReview(feedbackId, text);
    const updatedFeedback = result?.feedback;
    if (updatedFeedback?.id) {
      const mergeFeedback = (feedback) => (
        String(feedback.id) === String(updatedFeedback.id)
          ? { ...feedback, ...updatedFeedback }
          : feedback
      );
      setFeedbacks((current) => current.map(mergeFeedback));
      setAllFeedbacks((current) => current.map(mergeFeedback));
    }
    toast.success('Resposta enviada ao iFood e salva no FeedbackHero.');
    return result;
  };

  const handleAnswerGoogle = async (reviewId, text) => {
    const result = await replyGoogleBusinessReview(reviewId, text);
    setGoogleLive((current) => ({
      ...current,
      reviews: current.reviews.map((feedback) => (
        String(feedback.google_review_id) === String(result?.review_id || reviewId)
          ? { ...feedback, google_reply: result?.reply || { comment: text } }
          : feedback
      )),
    }));
    toast.success('Resposta publicada no Google.');
    return result;
  };

  return (
    <Box
      className="feedback-hero-root"
      sx={{
        mt: 0,
        px: { xs: 1.5, sm: 3 },
        pb: { xs: 6, sm: 8 },
      }}
    >
      <Box
        sx={{
          maxWidth: 1280,
          mx: 'auto',
          borderRadius: { xs: 2, sm: 3 },
          p: { xs: 1.5, sm: 2.5, md: 3 },
          overflow: 'hidden',
          background: `radial-gradient(900px circle at 10% -20%, rgba(251, 236, 214, 0.55) 0%, transparent 55%),
            radial-gradient(1000px circle at 90% 0%, rgba(214, 234, 248, 0.55) 0%, transparent 60%),
            ${feedbackHeroTokens.colors.pageBg}`,
          border: `1px solid ${feedbackHeroTokens.colors.softBorder}`,
        }}
      >
        <Stack alignItems="center" spacing={0.75} mb={{ xs: 2, sm: 2.5 }} textAlign="center">
          <Stack direction="row" spacing={1.25} alignItems="center">
            <CampaignOutlinedIcon sx={{ color: colors.text, fontSize: 26 }} />
            <Typography
              sx={{
                fontFamily: fonts.display,
                fontWeight: 700,
                color: colors.text,
                fontSize: { xs: '1.4rem', sm: '1.7rem' },
                letterSpacing: '-0.01em',
              }}
            >
              Painel de feedbacks
            </Typography>
          </Stack>
          <Typography
            sx={{
              fontFamily: fonts.body,
              color: colors.subtext,
              fontSize: { xs: '0.86rem', sm: '0.92rem' },
              maxWidth: 640,
              lineHeight: 1.5,
            }}
          >
            Visão consolidada de volume, qualidade e sentimento das avaliações dos seus clientes.
          </Typography>
          <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="center" flexWrap="wrap" useFlexGap>
            <Button
              variant="contained"
              size="small"
              startIcon={syncingIfood ? <CircularProgress size={15} color="inherit" /> : <CloudDownloadOutlinedIcon />}
              onClick={handleSyncIfood}
              disabled={syncingIfood || ifoodAvailable === null}
              sx={{
                mt: 0.5,
                borderRadius: 1.5,
                textTransform: 'none',
                fontFamily: fonts.body,
                fontWeight: 800,
                bgcolor: colors.text,
                '&:hover': { bgcolor: '#244638' },
              }}
            >
              {ifoodAvailable === null
                ? 'Verificando integração...'
                : !ifoodAvailable
                ? 'Conectar iFood'
                : syncingIfood
                  ? 'Atualizando avaliações...'
                  : ifoodSyncState?.enabled
                    ? 'Atualizar avaliações do iFood'
                    : 'Trazer avaliações do iFood'}
            </Button>
            <Tooltip
              arrow
              enterTouchDelay={0}
              title="Depois da primeira importação, o FeedbackHero verifica as avaliações recentes ao abrir o painel, no máximo uma vez a cada 15 minutos. Você também pode atualizar manualmente quando quiser."
            >
              <InfoOutlinedIcon sx={{ fontSize: 18, color: colors.subtext, cursor: 'help' }} />
            </Tooltip>
            <Button
              variant="outlined"
              size="small"
              startIcon={googleLive.loading ? <CircularProgress size={15} color="inherit" /> : <ReviewsOutlinedIcon />}
              onClick={handleRefreshGoogle}
              disabled={!googleLive.checked || googleLive.loading}
              sx={{
                mt: 0.5,
                borderRadius: 1.5,
                textTransform: 'none',
                fontFamily: fonts.body,
                fontWeight: 800,
                borderColor: '#AECBFA',
                color: '#174EA6',
                bgcolor: '#FFFFFF',
                '&:hover': { borderColor: '#1A73E8', bgcolor: '#F8FAFF' },
              }}
            >
              {!googleLive.checked
                ? 'Verificando Google...'
                : !googleLive.ready
                  ? googleLive.connected ? 'Selecionar ficha Google' : 'Conectar Google'
                  : googleLive.loading
                    ? 'Atualizando Google...'
                    : 'Atualizar avaliações do Google'}
            </Button>
            <Tooltip
              arrow
              enterTouchDelay={0}
              title="As avaliações do Google são consultadas ao vivo e não são gravadas. O comentário só é enviado à IA quando você abre o modal de resposta."
            >
              <InfoOutlinedIcon sx={{ fontSize: 18, color: colors.subtext, cursor: 'help' }} />
            </Tooltip>
          </Stack>
        </Stack>

        <Box sx={{ mb: { xs: 2, sm: 2.5 } }}>
          <FeedbackFilters
            filters={filters}
            ordem={ordem}
            onFiltersChange={handleFiltersChange}
            onOrderChange={handleOrderChange}
            onClearFilters={handleClearFilters}
          />
        </Box>

        {googleLive.error && (
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
            {googleLive.error} As demais avaliações continuam disponíveis normalmente.
          </Alert>
        )}

        <Grid container spacing={{ xs: 2, sm: 2.5 }}>
          <FeedbackResumoCard
            resumo={dashboardData.summary}
            onShowNegativos={handleShowAttention}
          />
          <FeedbackPointsChart feedbacks={feedbacks} pontos={pontos} googleExcluded={googleLive.ready} />
          <FeedbackOrigemCard feedbacks={dashboardData.origins} />
          <FeedbackSentimentDistribution resumo={dashboardData.summary} half />
          <FeedbackList
            feedbacks={dashboardData.feedbacks}
            total={dashboardData.total}
            page={page}
            pageSize={DEFAULT_PAGE_SIZE}
            ordem={ordem}
            attentionOnly={attentionOnly}
            onPageChange={(value) => setPage(value)}
            onClearAttentionMode={handleClearAttentionMode}
            onMarkAtendido={handleMarkFeedbackAtendido}
            resolvingFeedbackId={resolvingFeedbackId}
            onAnswerIfood={handleAnswerIfood}
            onAnswerGoogle={handleAnswerGoogle}
          />
        </Grid>

        <Dialog
          open={Boolean(ifoodSyncResult)}
          onClose={() => setIfoodSyncResult(null)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle sx={{ fontWeight: 800 }}>Atualização de avaliações do iFood</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 0.5 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">Loja consultada</Typography>
                <Typography fontWeight={700}>
                  {ifoodSyncResult?.merchant?.name || 'Loja iFood conectada'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Período consultado</Typography>
                <Typography fontWeight={700}>
                  {formatSyncDateTime(ifoodSyncResult?.date_from)} até{' '}
                  {formatSyncDateTime(ifoodSyncResult?.date_to || ifoodSyncResult?.synchronized_at)}
                </Typography>
              </Box>
              {ifoodSyncReviewCount > 0 ? (
                <Alert severity="success">
                  {ifoodSyncReviewCount}{' '}
                  {ifoodSyncReviewCount === 1
                    ? 'avaliação foi recebida e salva ou atualizada'
                    : 'avaliações foram recebidas e salvas ou atualizadas'} no FeedbackHero.
                </Alert>
              ) : (
                <Alert severity="info">
                  O iFood não retornou avaliações para esta loja no período consultado. A API do
                  iFood disponibiliza até 90 dias de histórico; avaliações mais antigas ainda podem
                  aparecer no Portal do Parceiro sem estarem disponíveis para importação.
                </Alert>
              )}
              {ifoodSyncClassifiedCount > 0 && (
                <Alert severity="success">
                  {ifoodSyncClassifiedCount}{' '}
                  {ifoodSyncClassifiedCount === 1
                    ? 'avaliação foi analisada para atualizar os temas mais comentados.'
                    : 'avaliações foram analisadas para atualizar os temas mais comentados.'}
                </Alert>
              )}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setIfoodSyncResult(null)} variant="contained">
              Entendi
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default FeedbackHero;
